import React, { useState, useEffect, useMemo } from 'react'
import { Text, Heading, Input, Select, SortableTableHeader, LoadingSpinner, Button } from '../components/ui'
import { Sidebar } from '../components/ui'
import { PermissionGuard } from '../components/PermissionGuard'
import { supabase } from '../lib/supabase'
import { 
  ExclamationTriangleIcon, 
  CheckCircleIcon, 
  XMarkIcon,
  ServerIcon,
  DocumentTextIcon,
  XCircleIcon,
  DocumentArrowUpIcon
} from '@heroicons/react/24/outline'
import { BRAND_COLOR } from '../constants'

interface DocumentData {
  id: number
  json: any
  type: string
  created_at: string
  human_observation?: string
  human_process_number?: string
}

const LeitorArquivos = () => {
  // Estados de abas
  const [activeTab, setActiveTab] = useState<'enviar' | 'historico'>('enviar')
  
  // Estados para upload de documento
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [modelo, setModelo] = useState<string>('')
  const [uploading, setUploading] = useState(false)
  const [uploadMessage, setUploadMessage] = useState('')
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [uploadResponseJson, setUploadResponseJson] = useState<any>(null)
  
  // Estados para curadoria
  const [humanObservation, setHumanObservation] = useState<string>('')
  const [humanProcessNumber, setHumanProcessNumber] = useState<string>('')
  const [savingDocument, setSavingDocument] = useState(false)
  const [documentSavedId, setDocumentSavedId] = useState<number | null>(null)
  
  const [documentsData, setDocumentsData] = useState<DocumentData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filtroTipo, setFiltroTipo] = useState('')
  const [filtroJson, setFiltroJson] = useState('')
  const [filtroStatus, setFiltroStatus] = useState<'com-dados' | 'sem-dados' | ''>('')
  const [tiposDisponiveis, setTiposDisponiveis] = useState<string[]>([])
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState<DocumentData | null>(null)
  
  // Estados de ordenação
  const [sortBy, setSortBy] = useState<'id' | 'type' | 'created_at'>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  
  // Estados de paginação
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from('documents_ocr')
          .select('*')
          .order('created_at', { ascending: false })

        if (error) {
          throw error
        }

        setDocumentsData(data || [])

        // Extrair tipos únicos para o dropdown
        const tipos = (data || []).map((d: any) => d.type).filter((t: any) => !!t)
        const tiposUnicos = Array.from(new Set(tipos)).sort((a: string, b: string) => a.localeCompare(b))
        setTiposDisponiveis(tiposUnicos)
      } catch (err: any) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }
    fetchDocuments()
  }, [])

  const documentsFiltrados = useMemo(() => {
    const filtered = documentsData.filter(item => {
      const matchTipo = filtroTipo === '' || item.type === filtroTipo
      const matchJson = !filtroJson || (item.json && JSON.stringify(item.json).toLowerCase().includes(filtroJson.toLowerCase()))
      const matchStatus =
        filtroStatus === '' ||
        (filtroStatus === 'com-dados' && !!item.json) ||
        (filtroStatus === 'sem-dados' && !item.json)
      
      return matchTipo && matchJson && matchStatus
    })

    // Aplicar ordenação
    return filtered.sort((a, b) => {
      let aValue: any = a[sortBy]
      let bValue: any = b[sortBy]

      // Tratamento especial para campos específicos
      if (sortBy === 'created_at') {
        aValue = new Date(aValue).getTime()
        bValue = new Date(bValue).getTime()
      } else if (sortBy === 'id') {
        aValue = Number(aValue)
        bValue = Number(bValue)
      } else {
        aValue = String(aValue).toLowerCase()
        bValue = String(bValue).toLowerCase()
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
  }, [documentsData, filtroTipo, filtroJson, filtroStatus, sortBy, sortOrder])

  // Resetar página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1)
  }, [filtroTipo, filtroJson, filtroStatus])

  const limparFiltros = () => {
    setFiltroTipo('')
    setFiltroJson('')
    setFiltroStatus('')
  }

  // Calcular dados da paginação
  const totalPages = Math.ceil(documentsFiltrados.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const documentsPaginados = documentsFiltrados.slice(startIndex, endIndex)

  const handleRowClick = (item: DocumentData) => {
    setSelectedDocument(item)
    setIsDetailsModalOpen(true)
  }

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false)
    setSelectedDocument(null)
  }

  const toggleSort = (key: string) => {
    if (sortBy === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key as any)
      setSortOrder('asc')
    }
  }

  // Handlers de paginação
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const getJsonIcon = (json: any) => {
    if (!json) return <ServerIcon className="h-5 w-5 text-gray-500" />
    return <CheckCircleIcon className="h-5 w-5 text-green-500" />
  }

  const getJsonText = (json: any) => {
    if (!json) return 'Sem dados'
    return 'Com dados'
  }

  const getJsonColor = (json: any) => {
    if (!json) return 'text-gray-600 dark:text-gray-400'
    return 'text-green-600 dark:text-green-400'
  }

  const getTypeIcon = (type: string) => {
    return <DocumentTextIcon className="h-5 w-5 text-blue-500" />
  }

  const getTypeText = (type: string) => {
    return type || 'Não definido'
  }

  const getTypeColor = (type: string) => {
    return 'text-blue-600 dark:text-blue-400'
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatJson = (data: any) => {
    if (!data) return 'Sem dados'
    try {
      // Se já é uma string, tentar fazer parse primeiro
      let parsedData = data
      if (typeof data === 'string') {
        try {
          parsedData = JSON.parse(data)
        } catch {
          // Se não conseguir fazer parse, usar a string original
          parsedData = data
        }
      }
      
      // Formatar JSON com indentação de 2 espaços
      const formatted = JSON.stringify(parsedData, null, 2)
      
      return formatted
    } catch (error) {
      // Se falhar, retornar a string original
      return String(data)
    }
  }

  const formatJsonWithBold = (data: any) => {
    if (!data) return 'Sem dados'
    try {
      const formatted = formatJson(data)
      
      // Destacar nomes dos campos em negrito
      return formatted
        .replace(/"([^"]+)":/g, '"<strong>$1</strong>":')  // Nomes dos campos em negrito
    } catch (error) {
      return String(data)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const formatDocumentType = (tipo: string) => {
    const tipos: { [key: string]: string } = {
      'nota_fiscal': 'Nota Fiscal',
      'nota_servico': 'Nota de Serviço',
      'cupom_fiscal': 'Cupom Fiscal',
      'certidao_nascimento': 'Certidão de Nascimento',
      'certidao_obito': 'Certidão de Óbito',
      'outros_documentos': 'Outros Documentos'
    }
    return tipos[tipo] || tipo
  }

  const renderStructuredData = (data: any) => {
    if (!data) return null

    // Se for array, processar cada item
    const items = Array.isArray(data) ? data : [data]

    return (
      <div className="space-y-4">
        {items.map((item: any, index: number) => {
          // Se o item tem documentos, processar cada documento
          const documentos = item.documentos || (item.documento ? [item.documento] : [item])
          
          return (
            <div key={index} className="space-y-4">
              {documentos.map((doc: any, docIndex: number) => (
                <div key={docIndex} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm">
                  {/* Cabeçalho do Documento */}
                  <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <Text size="lg" className="text-gray-900 dark:text-white font-semibold">
                          {formatDocumentType(doc.tipoDocumento || doc.tipo || 'Documento')}
                        </Text>
                        {doc.id && (
                          <Text size="sm" className="text-gray-500 dark:text-gray-400 mt-1">
                            ID: {doc.id}
                          </Text>
                        )}
                      </div>
                    </div>
                    
                    {/* Linha com Data/Hora, Emissor e Status */}
                    <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
                      {/* Data e Hora de Emissão */}
                      {(doc.dataEmissao || doc.horaEmissao) && (
                        <div className="flex-1">
                          <Text size="xs" className="text-gray-500 dark:text-gray-400 mb-1">
                            Data e Hora de Emissão
                          </Text>
                          <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                            {doc.dataEmissao && new Date(doc.dataEmissao).toLocaleDateString('pt-BR')}
                            {doc.dataEmissao && doc.horaEmissao && ' às '}
                            {doc.horaEmissao}
                          </Text>
                        </div>
                      )}

                      {/* Emissor */}
                      {doc.emissor && (
                        <div className="flex-1">
                          <Text size="xs" className="text-gray-500 dark:text-gray-400 mb-1">
                            Emissor
                          </Text>
                          {doc.emissor.nome && (
                            <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                              {doc.emissor.nome}
                            </Text>
                          )}
                          {doc.emissor.documento && (
                            <Text size="xs" className="text-gray-600 dark:text-gray-400">
                              {doc.emissor.documento}
                            </Text>
                          )}
                        </div>
                      )}

                      {/* Status Válido/Inválido */}
                      <div className="flex-shrink-0">
                        <Text size="xs" className="text-gray-500 dark:text-gray-400 mb-1">
                          Status
                        </Text>
                        <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                          doc.valido === false 
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                            : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        }`}>
                          {doc.valido === false ? 'Inválido' : 'Válido'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Valores */}
                  {doc.valores && (
                    <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <Text size="sm" className="text-gray-600 dark:text-gray-400 mb-3 font-semibold">
                        Valores
                      </Text>
                      <div className="grid grid-cols-2 gap-3">
                        {doc.valores.valorTotalProdutos !== undefined && (
                          <div>
                            <Text size="xs" className="text-gray-500 dark:text-gray-400">
                              Total de Produtos
                            </Text>
                            <Text size="md" className="text-gray-900 dark:text-white font-medium">
                              {formatCurrency(doc.valores.valorTotalProdutos)}
                            </Text>
                          </div>
                        )}
                        {doc.valores.valorServico !== undefined && (
                          <div>
                            <Text size="xs" className="text-gray-500 dark:text-gray-400">
                              Valor de Serviço
                            </Text>
                            <Text size="md" className="text-gray-900 dark:text-white font-medium">
                              {formatCurrency(doc.valores.valorServico)}
                            </Text>
                          </div>
                        )}
                        {doc.valores.valorTotalDocumento !== undefined && (
                          <div>
                            <Text size="xs" className="text-gray-500 dark:text-gray-400">
                              Total do Documento
                            </Text>
                            <Text size="md" className="text-gray-900 dark:text-white font-medium">
                              {formatCurrency(doc.valores.valorTotalDocumento)}
                            </Text>
                          </div>
                        )}
                        {doc.valores.valorElegivelDocumento !== undefined && (
                          <div>
                            <Text size="xs" className="text-gray-500 dark:text-gray-400">
                              Valor Elegível
                            </Text>
                            <Text size="md" className="text-green-700 dark:text-green-400 font-bold">
                              {formatCurrency(doc.valores.valorElegivelDocumento)}
                            </Text>
                          </div>
                        )}
                      </div>
                      {doc.valores.valorTotalFormatado && (
                        <div className="mt-3 pt-3 border-t border-blue-200 dark:border-blue-800">
                          <Text size="md" className="text-gray-700 dark:text-gray-300 font-medium">
                            {doc.valores.valorTotalFormatado}
                          </Text>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Itens */}
                  {doc.itens && Array.isArray(doc.itens) && doc.itens.length > 0 && (() => {
                    // Calcular total de itens elegíveis
                    const itensElegiveis = doc.itens.filter((item: any) => item.elegivelReembolso === true)
                    const totalItensElegiveis = itensElegiveis.reduce((total: number, item: any) => {
                      return total + (item.valorTotal || 0)
                    }, 0)
                    
                    return (
                      <div className="mb-4">
                        <Text size="sm" className="text-gray-600 dark:text-gray-400 mb-3 font-semibold">
                          Itens ({doc.itens.length})
                        </Text>
                        <div className="space-y-2">
                          {doc.itens.map((item: any, itemIndex: number) => (
                            <div key={itemIndex} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Text size="xs" className="text-gray-500 dark:text-gray-400">
                                      Item {item.item || itemIndex + 1}
                                    </Text>
                                    {item.elegivelReembolso !== undefined && (
                                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                        item.elegivelReembolso
                                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                          : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                      }`}>
                                        {item.elegivelReembolso ? 'Elegível' : 'Não Elegível'}
                                      </span>
                                    )}
                                  </div>
                                  <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                                    {item.descricao || 'Sem descrição'}
                                  </Text>
                                  {item.categoriaElegivel && (
                                    <Text size="xs" className="text-gray-500 dark:text-gray-400 mt-1">
                                      Categoria: {item.categoriaElegivel}
                                    </Text>
                                  )}
                                </div>
                              </div>
                              <div className="grid grid-cols-3 gap-3 mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                                {item.quantidade !== undefined && (
                                  <div>
                                    <Text size="xs" className="text-gray-500 dark:text-gray-400">
                                      Quantidade
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                                      {item.quantidade}
                                    </Text>
                                  </div>
                                )}
                                {item.valorUnitario !== undefined && (
                                  <div>
                                    <Text size="xs" className="text-gray-500 dark:text-gray-400">
                                      Valor Unitário
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                                      {formatCurrency(item.valorUnitario)}
                                    </Text>
                                  </div>
                                )}
                                {item.valorTotal !== undefined && (
                                  <div>
                                    <Text size="xs" className="text-gray-500 dark:text-gray-400">
                                      Valor Total
                                    </Text>
                                    <Text size="sm" className="text-gray-900 dark:text-white font-semibold">
                                      {formatCurrency(item.valorTotal)}
                                    </Text>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Total de Itens Elegíveis */}
                        {itensElegiveis.length > 0 && (
                          <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                            <div className="flex items-center justify-between">
                              <div>
                                <Text size="sm" className="text-gray-600 dark:text-gray-400 font-semibold">
                                  Total de Itens Elegíveis
                                </Text>
                                <Text size="xs" className="text-gray-500 dark:text-gray-400 mt-1">
                                  {itensElegiveis.length} {itensElegiveis.length === 1 ? 'item elegível' : 'itens elegíveis'}
                                </Text>
                              </div>
                              <div className="text-right">
                                <Text size="lg" className="text-green-700 dark:text-green-400 font-bold">
                                  {formatCurrency(totalItensElegiveis)}
                                </Text>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })()}

                  {/* Outros campos que não foram tratados especificamente */}
                  {Object.keys(doc).filter(key => 
                    !['id', 'tipoDocumento', 'tipo', 'valido', 'dataEmissao', 'horaEmissao', 'emissor', 'valores', 'itens'].includes(key)
                  ).length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <Text size="sm" className="text-gray-600 dark:text-gray-400 mb-2 font-medium">
                        Outras Informações
                      </Text>
                      <div className="space-y-1">
                        {Object.keys(doc).filter(key => 
                          !['id', 'tipoDocumento', 'tipo', 'valido', 'dataEmissao', 'horaEmissao', 'emissor', 'valores', 'itens'].includes(key)
                        ).map((key) => (
                          <div key={key} className="flex justify-between">
                            <Text size="xs" className="text-gray-500 dark:text-gray-400 capitalize">
                              {key.replace(/([A-Z])/g, ' $1').trim()}:
                            </Text>
                            <Text size="xs" className="text-gray-900 dark:text-white">
                              {typeof doc[key] === 'object' ? JSON.stringify(doc[key]) : String(doc[key])}
                            </Text>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        })}
      </div>
    )
  }

  // Handlers para upload de documento
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      setUploadStatus('idle')
      setUploadMessage('')
      setUploadResponseJson(null)
      setHumanObservation('')
      setHumanProcessNumber('')
      setDocumentSavedId(null)
    }
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
    setUploadStatus('idle')
    setUploadMessage('')
    setUploadResponseJson(null)
    setHumanObservation('')
    setHumanProcessNumber('')
    setDocumentSavedId(null)
  }

  const handleEnviarDocumento = async () => {
    if (!selectedFile) {
      setUploadStatus('error')
      setUploadMessage('Por favor, selecione um arquivo.')
      return
    }

    if (!modelo) {
      setUploadStatus('error')
      setUploadMessage('Por favor, selecione um modelo.')
      return
    }

    setUploading(true)
    setUploadStatus('idle')
    setUploadMessage('Enviando documento...')

    try {
      const formData = new FormData()
      formData.append('arquivo', selectedFile)
      formData.append('modelo', modelo)

      const response = await fetch('https://webhook.pasiai.com/webhook/OCR', {
        method: 'POST',
        body: formData
      })

      if (!response.ok) {
        throw new Error(`Erro ao enviar documento: ${response.status} ${response.statusText}`)
      }

      const result = await response.json()
      
      setUploadStatus('success')
      setUploadMessage('Documento processado com sucesso!')
      setUploadResponseJson(result)
      
      // Limpar campos de curadoria para novo documento
      setHumanObservation('')
      setHumanProcessNumber('')
      setDocumentSavedId(null)
      
      // Não limpar o formulário ainda - manter para permitir salvamento
    } catch (err: any) {
      setUploadStatus('error')
      setUploadMessage(err.message || 'Erro ao enviar documento. Tente novamente.')
    } finally {
      setUploading(false)
    }
  }

  const handleSaveDocument = async () => {
    if (!uploadResponseJson) {
      setUploadStatus('error')
      setUploadMessage('Não há dados para salvar. Por favor, envie um documento primeiro.')
      return
    }

    if (!modelo) {
      setUploadStatus('error')
      setUploadMessage('Tipo de documento não definido.')
      return
    }

    setSavingDocument(true)

    try {
      // Garantir que o JSON seja salvo corretamente (como objeto para JSONB ou string para TEXT)
      const documentToSave: any = {
        json: uploadResponseJson, // Supabase converterá automaticamente para JSONB se o campo for JSONB
        type: modelo,
        human_observation: humanObservation || null,
        human_process_number: humanProcessNumber || null
      }

      const { data, error } = await supabase
        .from('documents_ocr')
        .insert(documentToSave)
        .select('id')
        .single()

      if (error) {
        throw error
      }

      setDocumentSavedId(data.id)
      setUploadStatus('success')
      setUploadMessage('Documento salvo com sucesso!')

      // Atualizar lista de documentos
      const { data: updatedData, error: fetchError } = await supabase
        .from('documents_ocr')
        .select('*')
        .order('created_at', { ascending: false })

      if (!fetchError && updatedData) {
        setDocumentsData(updatedData)
        
        // Extrair tipos únicos para o dropdown
        const tipos = updatedData.map((d: any) => d.type).filter((t: any) => !!t)
        const tiposUnicos = Array.from(new Set(tipos)).sort((a: string, b: string) => a.localeCompare(b))
        setTiposDisponiveis(tiposUnicos)
      }

      // Limpar formulário após salvar com sucesso
      setSelectedFile(null)
      setModelo('')
      setUploadResponseJson(null)
      setHumanObservation('')
      setHumanProcessNumber('')
      
      // Limpar input de arquivo
      const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement
      if (fileInput) {
        fileInput.value = ''
      }
    } catch (err: any) {
      setUploadStatus('error')
      setUploadMessage(err.message || 'Erro ao salvar documento. Tente novamente.')
    } finally {
      setSavingDocument(false)
    }
  }

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <Text size="lg" className="text-gray-600 dark:text-gray-400">
              Carregando documentos...
            </Text>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-500 mx-auto mb-4" />
            <Text size="lg" className="text-red-600 dark:text-red-400">
              Erro ao carregar documentos: {error}
            </Text>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard 
          moduleName="documentos_leitor" 
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                  Acesso Negado
                </Text>
                <Text size="sm" color="muted">
                  Você não tem permissão para acessar esta página.
                </Text>
              </div>
            </div>
          }
        >
          <div className="h-full flex flex-col">
              {/* Header */}
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                  Leitor de Arquivos
                </Heading>
                <Text size="sm" color="muted" className="mt-2">
                  Envie documentos para processamento ou visualize o histórico
                </Text>
              </div>

              {/* Navegação de Abas */}
              <div className="px-6 border-b border-gray-200 dark:border-gray-700">
                <nav className="flex space-x-8">
                  <button
                    onClick={() => setActiveTab('enviar')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'enviar'
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <DocumentArrowUpIcon className="h-5 w-5" />
                      <span>Enviar Documento</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('historico')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'historico'
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      <DocumentTextIcon className="h-5 w-5" />
                      <span>Histórico</span>
                    </div>
                  </button>
                </nav>
              </div>

              {/* Conteúdo das Abas */}
              {activeTab === 'enviar' && (
                <div className="flex-1 px-6 py-4 overflow-y-auto">
                  <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                    <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white mb-6">
                      Enviar Documento
                    </Heading>
                    
                    <div className="space-y-6">
                      {/* Linha com Arquivo, Modelo, Botão e Mensagem */}
                      <div className="flex flex-col md:flex-row gap-4 items-end">
                        {/* Input de Arquivo */}
                        <div className="flex-1 w-full md:w-auto">
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Arquivo
                          </Text>
                          {!selectedFile ? (
                            <div className="mt-1">
                              <label className="flex flex-col items-center justify-center w-full h-20 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 dark:border-gray-600">
                                <div className="flex flex-col items-center justify-center py-3">
                                  <DocumentArrowUpIcon className="w-6 h-6 mb-1 text-gray-400" />
                                  <Text size="xs" className="text-gray-500 dark:text-gray-400">
                                    <span className="font-semibold">Clique para selecionar</span>
                                  </Text>
                                </div>
                                <input
                                  type="file"
                                  className="hidden"
                                  onChange={handleFileSelect}
                                  accept=".pdf,.doc,.docx,.txt"
                                />
                              </label>
                            </div>
                          ) : (
                            <div className="mt-1 flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600">
                              <div className="flex items-center space-x-2 flex-1 min-w-0">
                                <DocumentTextIcon className="h-5 w-5 text-blue-500 flex-shrink-0" />
                                <div className="min-w-0">
                                  <Text size="xs" className="text-gray-900 dark:text-white font-medium truncate">
                                    {selectedFile.name}
                                  </Text>
                                  <Text size="xs" className="text-gray-500 dark:text-gray-400">
                                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                  </Text>
                                </div>
                              </div>
                              <button
                                onClick={handleRemoveFile}
                                className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 ml-2 flex-shrink-0"
                              >
                                <XCircleIcon className="h-5 w-5" />
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Select de Modelo */}
                        <div className="flex-1 w-full md:w-auto min-w-[200px]">
                          <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                            Modelo
                          </Text>
                          <Select
                            value={modelo}
                            onChange={(e) => setModelo(e.target.value)}
                          >
                            <option value="">Selecione um modelo</option>
                            <option value="nota">Nota (Notas Fiscais, Notas de Serviço e Cupons Fiscais)</option>
                            <option value="documento">Documentos (Certidão de Nascimento, de Óbito e outros documentos)</option>
                          </Select>
                        </div>

                        {/* Botão de Enviar */}
                        <div className="w-full md:w-auto">
                          <Button
                            onClick={handleEnviarDocumento}
                            disabled={!selectedFile || !modelo || uploading}
                            className="w-full md:w-auto"
                            style={{ backgroundColor: BRAND_COLOR }}
                          >
                            {uploading ? (
                              <div className="flex items-center justify-center space-x-2">
                                <LoadingSpinner size="sm" />
                                <span>Enviando...</span>
                              </div>
                            ) : (
                              'Enviar Documento'
                            )}
                          </Button>
                        </div>

                        {/* Mensagem de Envio */}
                        {uploading && (
                          <div className="w-full md:w-auto flex items-center">
                            <Text size="sm" className="text-blue-600 dark:text-blue-400">
                              ⏱️ Enviando documento...
                            </Text>
                          </div>
                        )}
                      </div>

                      {/* Feedback de Upload */}
                      {uploadMessage && (
                        <div
                          className={`p-4 rounded-lg ${
                            uploadStatus === 'success'
                              ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                              : uploadStatus === 'error'
                              ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                              : 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                          }`}
                        >
                          <div className="flex items-start space-x-3">
                            {uploadStatus === 'success' ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-500 dark:text-green-400 mt-0.5" />
                            ) : uploadStatus === 'error' ? (
                              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 dark:text-red-400 mt-0.5" />
                            ) : (
                              <LoadingSpinner size="sm" className="mt-0.5" />
                            )}
                            <div className="flex-1">
                              <Text
                                size="sm"
                                className={
                                  uploadStatus === 'success'
                                    ? 'text-green-700 dark:text-green-300'
                                    : uploadStatus === 'error'
                                    ? 'text-red-700 dark:text-red-300'
                                    : 'text-blue-700 dark:text-blue-300'
                                }
                              >
                                {uploadMessage}
                              </Text>
                              {uploadStatus === 'idle' && uploading && (
                                <Text
                                  size="xs"
                                  className="text-blue-600 dark:text-blue-400 mt-2 block"
                                >
                                  ⏱️ Este processo pode demorar um pouco se o arquivo for grande. Por favor, aguarde...
                                </Text>
                              )}
                            </div>
                          </div>
                          
                          {/* Campos de curadoria - aparecem após sucesso e antes dos dados estruturados */}
                          {uploadStatus === 'success' && uploadResponseJson && !documentSavedId && (
                            <div className="mt-4 pt-4 border-t border-green-200 dark:border-green-800">
                              <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-4 block font-semibold">
                                Informações Adicionais:
                              </Text>
                              
                              <div className="flex flex-col md:flex-row gap-4 items-end">
                                {/* Campo de Observação */}
                                <div className="flex-1 w-full md:w-auto">
                                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                                    Observação
                                  </Text>
                                  <Input
                                    type="text"
                                    placeholder="Digite uma observação sobre este documento (opcional)"
                                    value={humanObservation}
                                    onChange={(e) => setHumanObservation(e.target.value)}
                                    className="w-full"
                                  />
                                </div>

                                {/* Campo de Número do Processo */}
                                <div className="flex-1 w-full md:w-auto">
                                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                                    Número do Processo
                                  </Text>
                                  <Input
                                    type="text"
                                    placeholder="Digite o número do processo (opcional)"
                                    value={humanProcessNumber}
                                    onChange={(e) => setHumanProcessNumber(e.target.value)}
                                    className="w-full"
                                  />
                                </div>

                                {/* Botão Gravar */}
                                <div className="w-full md:w-auto">
                                  <Button
                                    onClick={handleSaveDocument}
                                    disabled={savingDocument}
                                    className="w-full md:w-auto"
                                    style={{ backgroundColor: BRAND_COLOR }}
                                  >
                                    {savingDocument ? (
                                      <div className="flex items-center justify-center space-x-2">
                                        <LoadingSpinner size="sm" />
                                        <span>Salvando...</span>
                                      </div>
                                    ) : (
                                      'Gravar Documento'
                                    )}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Mensagem de sucesso após salvar */}
                          {documentSavedId && (
                            <div className="mt-4 pt-4 border-t border-green-200 dark:border-green-800">
                              <div className="flex items-center space-x-2 text-green-700 dark:text-green-300">
                                <CheckCircleIcon className="h-5 w-5" />
                                <Text size="sm" className="font-medium">
                                  Documento salvo com sucesso! ID: {documentSavedId}
                                </Text>
                              </div>
                            </div>
                          )}
                          
                          {/* Exibir dados estruturados quando houver sucesso */}
                          {uploadStatus === 'success' && uploadResponseJson && (() => {
                            // Calcular total de valor elegível de todos os documentos
                            const items = Array.isArray(uploadResponseJson) ? uploadResponseJson : [uploadResponseJson]
                            let totalValorElegivel = 0
                            
                            items.forEach((item: any) => {
                              const documentos = item.documentos || (item.documento ? [item.documento] : [item])
                              documentos.forEach((doc: any) => {
                                if (doc.valores && doc.valores.valorElegivelDocumento !== undefined) {
                                  totalValorElegivel += doc.valores.valorElegivelDocumento
                                }
                              })
                            })
                            
                            return (
                              <div className="mt-4 pt-4 border-t border-green-200 dark:border-green-800">
                                <div className="mb-4">
                                  <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block font-semibold">
                                    Dados do Documento:
                                  </Text>
                                  {totalValorElegivel > 0 && (
                                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                                      <div className="flex items-center justify-between">
                                        <Text size="sm" className="text-gray-700 dark:text-gray-300 font-semibold">
                                          Total de Valor Elegível:
                                        </Text>
                                        <Text size="lg" className="text-green-700 dark:text-green-400 font-bold">
                                          {formatCurrency(totalValorElegivel)}
                                        </Text>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <div className="max-h-[600px] overflow-y-auto">
                                  {renderStructuredData(uploadResponseJson)}
                                </div>
                              </div>
                            )
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'historico' && (
                <div className="flex-1 flex flex-col overflow-y-auto">
                  {/* Topo Colorido - Valores */}
                  <div className="px-6 py-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">
                        {documentsFiltrados.length}
                      </Text>
                      <Text size="sm" className="text-white">
                        Total de Documentos
                      </Text>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">
                        {documentsFiltrados.filter(d => d.json).length}
                      </Text>
                      <Text size="sm" className="text-white">
                        Com Dados JSON
                      </Text>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-red-500 to-red-600 shadow-lg rounded-lg p-6 text-white">
                    <div className="text-center">
                      <Text size="3xl" className="text-white font-bold mb-2">
                        {documentsFiltrados.filter(d => !d.json).length}
                      </Text>
                      <Text size="sm" className="text-white">
                        Sem Dados JSON
                      </Text>
                    </div>
                    </div>
                  </div>
                </div>

                  {/* Filtros - estilo Middleware Parceiros */}
                  <div className="px-6 py-4">
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-4">
                    Filtros
                  </Heading>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Tipo
                      </Text>
                      <Select
                        value={filtroTipo}
                        onChange={(e) => setFiltroTipo(e.target.value)}
                      >
                        <option value="">Todos os tipos</option>
                        {tiposDisponiveis.map((tipo) => (
                          <option key={tipo} value={tipo}>{tipo}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Conteúdo JSON (busca textual)
                      </Text>
                      <Input
                        type="text"
                        placeholder="Digite parte do conteúdo JSON..."
                        value={filtroJson}
                        onChange={(e) => setFiltroJson(e.target.value)}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                        Status dos Dados
                      </Text>
                      <Select
                        value={filtroStatus}
                        onChange={(e) => setFiltroStatus(e.target.value as any)}
                      >
                        <option value="">Todos os status</option>
                        <option value="com-dados">Com dados JSON</option>
                        <option value="sem-dados">Sem dados JSON</option>
                      </Select>
                    </div>
                    <div className="flex items-end">
                      <Button
                        onClick={limparFiltros}
                        className="w-full"
                        style={{ backgroundColor: BRAND_COLOR }}
                      >
                        Limpar Filtros
                      </Button>
                    </div>
                  </div>
                </div>
                  </div>

                  {/* Tabela */}
                  <div className="px-6 py-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                      Histórico de Documentos
                    </Heading>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                        <tr>
                          <SortableTableHeader
                            sortKey="id"
                            currentSort={sortBy}
                            sortOrder={sortOrder}
                            onSort={toggleSort}
                            className="w-1/4"
                          >
                            ID
                          </SortableTableHeader>
                          <SortableTableHeader
                            sortKey="type"
                            currentSort={sortBy}
                            sortOrder={sortOrder}
                            onSort={toggleSort}
                            className="w-1/4"
                          >
                            Tipo
                          </SortableTableHeader>
                          <SortableTableHeader
                            sortKey="created_at"
                            currentSort={sortBy}
                            sortOrder={sortOrder}
                            onSort={toggleSort}
                            className="w-1/4 text-center"
                          >
                            Data
                          </SortableTableHeader>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-1/4">
                            Dados JSON
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {documentsPaginados.map((item) => (
                          <tr 
                            key={item.id} 
                            className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                            onClick={() => handleRowClick(item)}
                          >
                            <td className="px-4 py-4 text-left">
                              <Text size="sm" className="text-gray-900 dark:text-white font-medium truncate block">
                                {item.id}
                              </Text>
                            </td>
                            <td className="px-4 py-4 text-left">
                              <div className="flex items-center gap-2">
                                {getTypeIcon(item.type)}
                                <Text size="sm" className={`font-medium ${getTypeColor(item.type)}`}>
                                  {getTypeText(item.type)}
                                </Text>
                              </div>
                            </td>
                            <td className="px-4 py-4 text-center">
                              <Text size="sm" className="text-gray-500 dark:text-gray-400">
                                {formatDate(item.created_at)}
                              </Text>
                            </td>
                            <td className="px-4 py-4 text-left">
                              <div className="flex items-center gap-2">
                                {getJsonIcon(item.json)}
                                <Text size="sm" className={`font-medium ${getJsonColor(item.json)}`}>
                                  {getJsonText(item.json)}
                                </Text>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Controles de Paginação */}
                  {totalPages > 1 && (
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                          <Text size="sm">
                            Mostrando {startIndex + 1} a {Math.min(endIndex, documentsFiltrados.length)} de {documentsFiltrados.length} registros
                          </Text>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={handlePreviousPage}
                            disabled={currentPage === 1}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            Anterior
                          </button>
                          
                          <div className="flex space-x-1">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                              let pageNumber: number;
                              if (totalPages <= 5) {
                                pageNumber = i + 1;
                              } else if (currentPage <= 3) {
                                pageNumber = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNumber = totalPages - 4 + i;
                              } else {
                                pageNumber = currentPage - 2 + i;
                              }
                              
                              return (
                                <button
                                  key={pageNumber}
                                  onClick={() => handlePageChange(pageNumber)}
                                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                                    currentPage === pageNumber
                                      ? 'bg-blue-600 text-white'
                                      : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                                  }`}
                                >
                                  {pageNumber}
                                </button>
                              );
                            })}
                          </div>
                          
                          <button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                          >
                            Próximo
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                  </div>
                </div>
              )}
            </div>
          </PermissionGuard>
      </div>

      {/* Modal de Detalhes */}
      {isDetailsModalOpen && selectedDocument && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseDetailsModal}></div>
            
            <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-6xl sm:p-6 max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
                    Detalhes do Documento
                  </Heading>
                  <Text size="sm" color="muted" className="mt-1">
                    ID: {selectedDocument.id} - Tipo: {selectedDocument.type}
                  </Text>
                </div>
                <button
                  onClick={handleCloseDetailsModal}
                  className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Tabela de Configuração */}
              <div className="flex-1 overflow-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Campo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Valor
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          ID
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {selectedDocument.id}
                        </Text>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Tipo
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <div className="flex items-center gap-2">
                          {getTypeIcon(selectedDocument.type)}
                          <Text size="sm" className={`font-medium ${getTypeColor(selectedDocument.type)}`}>
                            {getTypeText(selectedDocument.type)}
                          </Text>
                        </div>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Dados JSON
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <div className="max-w-7xl">
                          <div className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-xl shadow-lg overflow-hidden">
                            <div className="bg-gray-100 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                  <Text size="sm" className="text-gray-600 dark:text-gray-400 font-medium ml-2">
                                    Dados JSON
                                  </Text>
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-500">
                                  JSON
                                </div>
                              </div>
                            </div>
                            <div className="p-6">
                              <div
                                className="text-sm text-gray-900 dark:text-gray-100 font-mono overflow-auto max-h-96 whitespace-pre-wrap break-words leading-loose bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border"
                                dangerouslySetInnerHTML={{ __html: formatJsonWithBold(selectedDocument.json) }}
                              />
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Data de Criação
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <Text size="sm" className="text-gray-700 dark:text-gray-300">
                          {formatDate(selectedDocument.created_at)}
                        </Text>
                      </td>
                    </tr>
                    {selectedDocument.human_observation && (
                      <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap text-left">
                          <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                            Observação
                          </Text>
                        </td>
                        <td className="px-6 py-4 text-left">
                          <Text size="sm" className="text-gray-700 dark:text-gray-300">
                            {selectedDocument.human_observation}
                          </Text>
                        </td>
                      </tr>
                    )}
                    {selectedDocument.human_process_number && (
                      <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <td className="px-6 py-4 whitespace-nowrap text-left">
                          <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                            Número do Processo
                          </Text>
                        </td>
                        <td className="px-6 py-4 text-left">
                          <Text size="sm" className="text-gray-700 dark:text-gray-300">
                            {selectedDocument.human_process_number}
                          </Text>
                        </td>
                      </tr>
                    )}
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <Text size="sm" className="text-gray-900 dark:text-white font-medium">
                          Status dos Dados
                        </Text>
                      </td>
                      <td className="px-6 py-4 text-left">
                        <div className="flex items-center gap-2">
                          {getJsonIcon(selectedDocument.json)}
                          <Text size="sm" className={`font-medium ${getJsonColor(selectedDocument.json)}`}>
                            {getJsonText(selectedDocument.json)}
                          </Text>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LeitorArquivos
